import { useState, useRef, useEffect, useCallback } from "react";
import * as XLSX from "xlsx";
import { KpiModal, ModalHeader } from "./KpiIcons";
import { apiCall } from "../../../../_services/apiCall";
import config from "../../../../config/config.json";

const SPREADSHEET_ACCEPT = ".csv,.xlsx,.xls";
const DOCUMENT_ACCEPT    = ".pdf,.doc,.docx,.jpg,.jpeg,.png";
const ALL_ACCEPT         = `${SPREADSHEET_ACCEPT},${DOCUMENT_ACCEPT}`;

// All known methods — used as fallback when settings has no restriction
const ALL_CALC_METHODS = [
  { id: "SPEND_BASED",    label: "Spend Based",    short: "Spend",    icon: "💰", color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe" },
  { id: "ACTIVITY_BASED", label: "Activity Based", short: "Activity", icon: "⚙️", color: "#0369a1", bg: "#eff6ff", border: "#bfdbfe" },
];

// KPI types that require emission category + calculation method
const EMISSION_KPI_TYPES = ["EMISSION", "EMISSION_ENERGY"];

// ── helpers ───────────────────────────────────────────────────────────────────
const parseDate = (v) => {
  if (!v) return "";
  if (!isNaN(v) && Number(v) > 40000) {
    try {
      const d = XLSX.SSF.parse_date_code(Number(v));
      if (d) return `${d.y}-${String(d.m).padStart(2,"0")}-${String(d.d).padStart(2,"0")}`;
    } catch { /* fall through */ }
  }
  const d = new Date(v);
  return isNaN(d.getTime()) ? String(v) : d.toISOString().slice(0, 10);
};

const getCol = (row, ...keys) => {
  for (const k of keys) {
    const match = Object.keys(row).find(rk => rk.trim().toLowerCase() === k.toLowerCase());
    if (match !== undefined && row[match] !== "") return String(row[match]).trim();
  }
  return "";
};

// ── tiny pure components ──────────────────────────────────────────────────────
const FieldLabel = ({ children, required }) => (
  <div style={{ fontSize:11, fontWeight:700, color:"var(--ink-3)", textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:"var(--font-mono)", marginBottom:6 }}>
    {children}
    {required && <span style={{ color:"var(--red)", marginLeft:2 }}>*</span>}
  </div>
);

const ProgressStrip = ({ pct }) => (
  <div style={{ height:4, background:"var(--rule)", borderRadius:2, overflow:"hidden", margin:"12px 0 4px" }}>
    <div style={{ height:"100%", borderRadius:2, background:"linear-gradient(90deg,var(--accent),#60a5fa)", width:`${pct}%`, transition:"width 0.25s ease" }} />
  </div>
);

// ── Emission Method Picker ────────────────────────────────────────────────────
// Shows when the question is an EMISSION/EMISSION_ENERGY KPI.
// availableMethods: string[] from settings (or ALL_CALC_METHODS as fallback)
// selected: string | null — single method picked by user
// onChange: (methodId: string) => void
const EmissionMethodPicker = ({ availableMethods, selected, onChange, categoryName, scopeName }) => {
  const methods = ALL_CALC_METHODS.filter(m =>
    availableMethods.length === 0 || availableMethods.includes(m.id)
  );

  return (
    <div>
      <FieldLabel required>Calculation Method</FieldLabel>

      {/* Context badge showing which emission category this is for */}
      {categoryName && (
        <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:10, padding:"6px 10px", background:"#f0faf5", border:"1px solid #a5dfc3", borderRadius:7 }}>
          <span style={{ fontSize:12 }}>🌍</span>
          <div>
            <span style={{ fontSize:11, fontWeight:700, color:"#1baa6f" }}>{scopeName}</span>
            <span style={{ fontSize:11, color:"#475569" }}> → {categoryName}</span>
          </div>
          {availableMethods.length > 0 && availableMethods.length < ALL_CALC_METHODS.length && (
            <span style={{ marginLeft:"auto", fontSize:10, color:"#64748b", background:"#e8f5e9", borderRadius:4, padding:"1px 7px", fontWeight:600 }}>
              {availableMethods.length} method{availableMethods.length > 1 ? "s" : ""} configured
            </span>
          )}
        </div>
      )}

      {/* Method pills — single select */}
      <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
        {methods.map(m => {
          const isActive = selected === m.id;
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => onChange(m.id)}
              style={{
                display:"inline-flex", alignItems:"center", gap:6,
                padding:"9px 18px", borderRadius:10, fontSize:12, fontWeight:isActive?700:500,
                border:`2px solid ${isActive?m.color:"#e2e8f0"}`,
                background: isActive ? m.bg : "#fafbfc",
                color: isActive ? m.color : "#64748b",
                cursor:"pointer", transition:"all 0.15s", fontFamily:"inherit",
                boxShadow: isActive ? `0 0 0 3px ${m.color}22` : "none",
                flex: methods.length <= 2 ? 1 : "none",
              }}
            >
              <span style={{ fontSize:16 }}>{m.icon}</span>
              <div style={{ textAlign:"left" }}>
                <div>{m.label}</div>
                <div style={{ fontSize:10, opacity:0.7, fontWeight:400 }}>
                  {m.id === "SPEND_BASED" ? "Cost / financial data" : "Physical activity data"}
                </div>
              </div>
              {isActive && (
                <span style={{ marginLeft:"auto", fontSize:13, color:m.color, flexShrink:0 }}>✓</span>
              )}
            </button>
          );
        })}
      </div>

      {!selected && (
        <div style={{ fontSize:11, color:"var(--red)", marginTop:6, fontWeight:600 }}>
          ⚠ Select a calculation method to continue
        </div>
      )}
    </div>
  );
};


/* ════════════════════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════════════════════ */
const AddDataModal = ({
  show,
  onHide,
  item,
  financialYearId,
  locations = [],
  onSave,
  saveAnswer,
}) => {
  const [step, setStep] = useState("choose");
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");
  const [parseProgress, setParseProgress] = useState(0);
  const [parseError,    setParseError]    = useState("");
  const [parsedMeta,    setParsedMeta]    = useState(null);
  const [dragOver,      setDragOver]      = useState(false);
  const [conflictPayload, setConflictPayload] = useState(null);

  // ── Emission method state ──────────────────────────────────────────────────
  // availableEmissionMethods: fetched from emissionKpiSettings for this category
  //   [] means "fetch in progress or not applicable"
  // configuredMethods: the methods the admin configured for this category
  //   [] means no restriction (show all); non-empty means show only those
  const [configuredMethods,  setConfiguredMethods]  = useState([]);
  const [emissionSettingsLoaded, setEmissionSettingsLoaded] = useState(false);
  const [selectedCalcMethod, setSelectedCalcMethod] = useState(null);

  // Is this an emission KPI?
  const isEmissionKpi = EMISSION_KPI_TYPES.includes(item?.kpi_type);
  const emissionCategoryId   = item?.emission_category_id   ?? null;
  const emissionCategoryName = item?.emission_category?.name ?? null;
  const emissionScopeName    = item?.emission_category?.scopeName
                            ?? item?.emission_category?.scope
                            ?? null;

  const resolvedUnits = item?.unitSetting?.supportedUnits?.length
    ? item.unitSetting.supportedUnits.map(u => u.unit) : [];
  const defaultUnit =
    item?.unitSetting?.selectedUnit ||
    item?.unitSetting?.baseUnit     ||
    resolvedUnits[0];

  const [form, setForm] = useState({
    location_ids: [], sub_location_ids: [],
    from_date: "", to_date: "",
    value: "", unit: defaultUnit,
    unit_category_id: item?.unitSetting?.id,
  });

  const [openLocDD,    setOpenLocDD]    = useState(false);
  const [openSubLocDD, setOpenSubLocDD] = useState(false);
  const locRef    = useRef(null);
  const subLocRef = useRef(null);
  const fileRef   = useRef(null);

  // ── Fetch emission KPI settings for this specific category ────────────────
  // Called once when modal opens and item is an emission KPI.
  // Finds the saved settings row for item.emission_category_id and extracts
  // calculation_methods[] — that array defines which methods are available.
  const fetchEmissionSettings = useCallback(async () => {
    if (!isEmissionKpi || !emissionCategoryId) {
      setConfiguredMethods([]);
      setEmissionSettingsLoaded(true);
      return;
    }
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}emissionKpiSettings`,
        {}, {}, "GET"
      );
      if (isSuccess) {
        const raw  = data;
        const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];

        // Find the settings row for this question's emission category
        const row = list.find(r =>
          r.emission_category_id === emissionCategoryId && r.is_enabled
        );

        if (row && Array.isArray(row.calculation_methods) && row.calculation_methods.length > 0) {
          // Admin has configured specific methods — restrict to those
          setConfiguredMethods(row.calculation_methods);
          // Auto-select if only one method is configured
          if (row.calculation_methods.length === 1) {
            setSelectedCalcMethod(row.calculation_methods[0]);
          }
        } else {
          // No restriction configured → show all available methods
          setConfiguredMethods([]);
        }
      }
    } catch {
      // Fail silently — fall back to showing all methods
      setConfiguredMethods([]);
    } finally {
      setEmissionSettingsLoaded(true);
    }
  }, [isEmissionKpi, emissionCategoryId]);

  // ── Reset on open ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (show) {
      setStep("choose");
      setError("");
      setParseError("");
      setParsedMeta(null);
      setParseProgress(0);
      setDragOver(false);
      setOpenLocDD(false);
      setOpenSubLocDD(false);
      setConflictPayload(null);
      setSelectedCalcMethod(null);
      setConfiguredMethods([]);
      setEmissionSettingsLoaded(false);
      setForm({
        location_ids: [], sub_location_ids: [],
        from_date: "", to_date: "", value: "",
        unit: defaultUnit, unit_category_id: item?.unitSetting?.id,
      });
      // Fetch emission settings when modal opens
      fetchEmissionSettings();
    }
  }, [show, item, defaultUnit, fetchEmissionSettings]);

  /* ── Click-outside dropdowns ── */
  useEffect(() => {
    const h = (e) => { if (locRef.current && !locRef.current.contains(e.target)) setOpenLocDD(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  useEffect(() => {
    const h = (e) => { if (subLocRef.current && !subLocRef.current.contains(e.target)) setOpenSubLocDD(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  /* ── Prune sub-locations when location changes ── */
  const activeSubLocGroups = (() => {
    if (!form.location_ids.length) return [];
    return form.location_ids
      .map(id => locations.find(l => l.id === id))
      .filter(loc => loc?.location?.subLocation?.length)
      .map(loc => ({ locId: loc.id, locName: loc.name, subs: loc.location.subLocation }));
  })();
  const activeSubLocations = activeSubLocGroups.flatMap(g => g.subs);
  const hasSubLocs         = activeSubLocations.length > 0;

  useEffect(() => {
    const validSubIds = new Set(activeSubLocations.map(s => s.id));
    setForm(p => ({ ...p, sub_location_ids: p.sub_location_ids.filter(id => validSubIds.has(id)) }));
  }, [form.location_ids.join(",")]); // eslint-disable-line

  const setField   = (key, val) => setForm(p => ({ ...p, [key]: val }));
  const toggleLoc  = (id) => setForm(p => ({ ...p, location_ids: p.location_ids.includes(id) ? p.location_ids.filter(x => x !== id) : [...p.location_ids, id] }));
  const toggleAllLocs = () => setForm(p => ({ ...p, location_ids: p.location_ids.length === locations.length ? [] : locations.map(l => l.id) }));
  const toggleSubLoc  = (id) => setForm(p => ({ ...p, sub_location_ids: p.sub_location_ids.includes(id) ? p.sub_location_ids.filter(x => x !== id) : [...p.sub_location_ids, id] }));
  const toggleAllSubLocs = () => setForm(p => ({ ...p, sub_location_ids: p.sub_location_ids.length === activeSubLocations.length ? [] : activeSubLocations.map(s => s.id) }));

  const locLabel = () => {
    if (!form.location_ids.length) return "Select locations…";
    if (form.location_ids.length === locations.length) return "All locations";
    const names = form.location_ids.map(id => locations.find(l => l.id === id)?.name).filter(Boolean);
    return names.length <= 2 ? names.join(", ") : `${names[0]}, +${names.length - 1} more`;
  };

  const subLocFullName = (id) => {
    for (const g of activeSubLocGroups) {
      const sl = g.subs.find(s => s.id === id);
      if (sl) return `${g.locName}-${sl.subLocation}`;
    }
    return null;
  };

  const subLocLabel = () => {
    const { sub_location_ids: ids } = form;
    if (!ids.length) return "Select sub-locations…";
    if (ids.length === activeSubLocations.length) return "All sub-locations";
    const names = ids.map(subLocFullName).filter(Boolean);
    return names.length <= 2 ? names.join(", ") : `${names[0]}, +${names.length - 1} more`;
  };

  /* ══ FILE HANDLING ══ */
  const handleFile = useCallback(async (file) => {
    if (!file) return;
    setParseError("");
    setStep("loading");
    setParseProgress(10);
    await uploadDocumentViaApi(file);
  }, []); // eslint-disable-line

  const uploadDocumentViaApi = async (file) => {
    try {
      setParseProgress(20);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("shouldParseDocument", "false");
      setParseProgress(40);

      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}documents/upload`,
        {}, formData, "POST"
      );
      setParseProgress(85);

      if (!isSuccess || !data) {
        setParseError("The server could not upload this document. Try a different file or enter manually.");
        setStep("choose");
        return;
      }

      setParseProgress(100);
      await new Promise(r => setTimeout(r, 150));

      setParsedMeta({
        source: "api", value: "", from_date: "", to_date: "", unit: defaultUnit,
        location_id: null, location_name: null,
        missing: ["value", "from_date", "to_date"],
        fileMetadataId: data.fileMetadataId,
        uploadedFileMetadata: {
          documentType:   data.documentType,
          fileMetadataId: data.fileMetadataId,
          fileMetadata:   data.fileMetadata,
          isExisting:     data.isExisting,
        },
      });
      setStep("form");
    } catch (e) {
      console.error("Document upload error:", e);
      setParseError("Upload failed. Check your connection and try again.");
      setStep("choose");
    } finally {
      setParseProgress(0);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  /* ══ BUILD PAYLOAD ══ */
  const buildPayload = () => {
    const resolvedFyId = financialYearId || item.financial_year_id;
    const subToLocMap  = {};
    activeSubLocGroups.forEach(g => {
      g.subs.forEach(sl => {
        if (form.sub_location_ids.includes(sl.id)) subToLocMap[sl.id] = g.locId;
      });
    });

    return {
      financial_year_id:  resolvedFyId,
      location_ids:       form.location_ids,
      ...(form.sub_location_ids.length
        ? { sub_location_ids: form.sub_location_ids, sub_location_to_location_map: subToLocMap }
        : {}),
      module:     item.module,
      questionId: item.id,
      from_date:  form.from_date,
      to_date:    form.to_date,
      value:      parseFloat(form.value),
      unit:       form.unit,
      unit_category_id: form.unit_category_id,
      // ── Emission fields ──────────────────────────────────────────────────
      // Only added when this is an emission KPI and user selected a method
      ...(isEmissionKpi && selectedCalcMethod
        ? {
            emission_category_id: emissionCategoryId,
            calculation_method:   selectedCalcMethod,
          }
        : {}),
      ...(parsedMeta?.uploadedFileMetadata
        ? { uploadedDocument: parsedMeta.uploadedFileMetadata }
        : {}),
    };
  };

  /* ══ SAVE ══ */
  const callSaveApi = (payload) =>
    apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}quantative-data`,
      {}, payload, "POST", false, false
    );

  const handleSave = async (isConfirmed = false) => {
    setError("");

    if (!isConfirmed) {
      if (!item)                         return setError("Invalid KPI item.");
      if (!form.location_ids.length)     return setError("Select at least one location.");
      if (!form.from_date || !form.to_date) return setError("Date range is required.");
      if (new Date(form.from_date) > new Date(form.to_date))
        return setError("From date cannot be after to date.");
      if (form.value === "" || isNaN(form.value))
        return setError("Value must be a valid number.");

      // Emission KPI — must select a calculation method
      if (isEmissionKpi && !selectedCalcMethod)
        return setError("Select a calculation method for this emission KPI.");

      const resolvedFyId = financialYearId || item.financial_year_id;
      if (!resolvedFyId)
        return setError("Financial year could not be determined. Please refresh.");
    }

    const payload = isConfirmed && conflictPayload
      ? { ...conflictPayload, confirmOverwrite: true }
      : buildPayload();

    setSaving(true);
    try {
      const result = await callSaveApi(payload);

      if (result?.status === 409 || result?.statusCode === 409) {
        setConflictPayload(payload);
        setStep("conflict");
        return;
      }

      if (!result?.isSuccess) {
        setError(result?.message || "Failed to save data. Please check your inputs.");
        if (step === "conflict") setStep("form");
        return;
      }

      onSave?.(item, payload);
      onHide();
    } catch (err) {
      console.error("Save Error:", err);
      setError("A network error occurred. Please try again.");
      if (step === "conflict") setStep("form");
    } finally {
      setSaving(false);
    }
  };

  /* ══ RENDER ══ */
  if (!show) return null;

  const hasParsed      = !!parsedMeta;
  const allFound       = hasParsed && parsedMeta.missing.length === 0;
  const isWarn         = (field) => hasParsed && parsedMeta.missing.includes(field);
  const hasUploadedDoc = hasParsed && !!parsedMeta.uploadedFileMetadata;
  const stepIndex      = step === "form" || step === "conflict" ? 1 : 0;

  const subLocSummary = form.sub_location_ids.length
    ? form.sub_location_ids.length === activeSubLocations.length
      ? "All sub-locations"
      : form.sub_location_ids.map(subLocFullName).filter(Boolean).join(", ")
    : null;

  // The methods available for this category (after settings fetch)
  // configuredMethods = [] → admin set no restriction → show ALL
  // configuredMethods = ["SPEND_BASED"] → show only Spend
  const availableMethodIds = configuredMethods.length > 0
    ? configuredMethods
    : ALL_CALC_METHODS.map(m => m.id);

  return (
    <KpiModal show={show} onClose={onHide} maxWidth={540}>
      <ModalHeader emoji="➕" title="Add Data" subtitle={item?.kpi_name} onClose={onHide} />

      {/* Step indicator */}
      <div style={{ display:"flex", alignItems:"center", gap:0, padding:"0 24px 14px" }}>
        {["Upload", "Fill & Save"].map((label, i) => {
          const active = i === stepIndex;
          const done   = i < stepIndex;
          return (
            <div key={label} style={{ display:"flex", alignItems:"center", flex:i===1?1:"none" }}>
              <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:3 }}>
                <div style={{ width:22, height:22, borderRadius:"50%", fontSize:10, fontWeight:800, fontFamily:"var(--font-mono)", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", background:done?"var(--green)":active?"var(--accent)":"var(--rule)", transition:"background 0.2s" }}>
                  {done ? "✓" : i + 1}
                </div>
                <span style={{ fontSize:9, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:"var(--font-mono)", color:active?"var(--accent)":"var(--ink-4)" }}>{label}</span>
              </div>
              {i === 0 && (
                <div style={{ width:40, height:2, borderRadius:1, margin:"0 8px 14px", background:done?"var(--green)":"var(--rule)", transition:"background 0.2s" }} />
              )}
            </div>
          );
        })}
      </div>

      <div style={{ padding:"0 24px 24px" }}>

        {/* ══ CHOOSE / LOADING ══ */}
        {(step === "choose" || step === "loading") && (
          <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => step !== "loading" && fileRef.current?.click()}
              style={{ border:`2px dashed ${dragOver?"var(--accent)":"var(--rule)"}`, borderRadius:12, padding:"32px 20px", textAlign:"center", cursor:step==="loading"?"default":"pointer", background:dragOver?"var(--accent-3)":"var(--surface-2)", transition:"all 0.15s ease" }}
            >
              <input ref={fileRef} type="file" hidden accept={ALL_ACCEPT} onChange={(e) => e.target.files[0] && handleFile(e.target.files[0])} />

              {step === "loading" ? (
                <>
                  <div style={{ fontSize:34, marginBottom:10 }}>⚙️</div>
                  <div style={{ fontWeight:700, fontSize:14, color:"var(--ink)", marginBottom:4 }}>Uploading your file…</div>
                  <ProgressStrip pct={parseProgress} />
                  <div style={{ fontSize:11, color:"var(--ink-4)", marginTop:6, fontFamily:"var(--font-mono)" }}>{parseProgress}%</div>
                </>
              ) : (
                <>
                  <div style={{ fontSize:38, marginBottom:10 }}>📂</div>
                  <div style={{ fontWeight:700, fontSize:14, color:"var(--ink)", marginBottom:8 }}>Drop a file or click to browse</div>
                  <div style={{ display:"flex", gap:8, justifyContent:"center", flexWrap:"wrap", marginBottom:12 }}>
                    {[{ icon:"📊", label:"XLSX / CSV", sub:"auto-fills form" }, { icon:"📄", label:"PDF / DOC", sub:"stored as proof" }, { icon:"🖼️", label:"JPG / PNG", sub:"stored as proof" }].map(({ icon, label, sub }) => (
                      <div key={label} style={{ padding:"6px 12px", borderRadius:8, background:"var(--surface)", border:"1px solid var(--rule)", textAlign:"left" }}>
                        <div style={{ fontSize:11, fontWeight:700, color:"var(--ink-2)" }}>{icon} {label}</div>
                        <div style={{ fontSize:10, color:"var(--ink-4)" }}>{sub}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display:"flex", gap:5, justifyContent:"center", flexWrap:"wrap" }}>
                    {["Value", "From Date", "To Date", "Unit", "Location"].map(col => (
                      <span key={col} style={{ fontSize:9.5, fontWeight:700, padding:"2px 7px", borderRadius:4, background:"var(--surface-3)", color:"var(--ink-3)", fontFamily:"var(--font-mono)", textTransform:"uppercase", letterSpacing:"0.05em" }}>{col}</span>
                    ))}
                  </div>
                </>
              )}
            </div>

            {parseError && (
              <div style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 14px", borderRadius:8, background:"var(--red-2)", border:"1px solid #fca5a5", fontSize:12, color:"var(--red)", fontWeight:600 }}>
                ⚠️ {parseError}
              </div>
            )}

            {step !== "loading" && (
              <>
                <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                  <div style={{ flex:1, height:1, background:"var(--rule)" }} />
                  <span style={{ fontSize:11, color:"var(--ink-4)", fontFamily:"var(--font-mono)" }}>OR</span>
                  <div style={{ flex:1, height:1, background:"var(--rule)" }} />
                </div>
                <button className="kpi-btn-secondary" onClick={() => setStep("form")} style={{ width:"100%", padding:"10px 0", fontSize:13 }}>
                  ✏️ Enter Manually
                </button>
              </>
            )}
          </div>
        )}

        {/* ══ CONFLICT ══ */}
        {step === "conflict" && (
          <div style={{ display:"flex", flexDirection:"column", gap:20, textAlign:"center", padding:"20px 0" }}>
            <div style={{ fontSize:52 }}>⚠️</div>
            <div>
              <h3 style={{ margin:"0 0 10px", fontSize:18, color:"var(--ink)" }}>Data Already Exists</h3>
              <p style={{ fontSize:13, color:"var(--ink-3)", lineHeight:"1.6", margin:0 }}>
                Records already exist for the selected location(s) within this date range.
                You can go back and adjust your inputs, or save these new records alongside the existing ones.
              </p>
            </div>
            <div style={{ background:"var(--surface-2)", padding:"12px 16px", borderRadius:10, fontSize:12, textAlign:"left", border:"1px solid var(--rule)", lineHeight:"1.8" }}>
              <div style={{ fontWeight:700, marginBottom:6, color:"var(--ink-2)", fontSize:11, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:"var(--font-mono)" }}>Summary</div>
              <div>📍 {form.location_ids.length === 1 ? locations.find(l => l.id === form.location_ids[0])?.name || "1 Location" : `${form.location_ids.length} Location(s)`}</div>
              {subLocSummary && <div>🏢 Sub-locations: {subLocSummary}</div>}
              <div>📅 {form.from_date} → {form.to_date}</div>
              <div>📊 {form.value} {form.unit}</div>
              {isEmissionKpi && selectedCalcMethod && (
                <div>🧮 Method: {ALL_CALC_METHODS.find(m => m.id === selectedCalcMethod)?.label || selectedCalcMethod}</div>
              )}
            </div>
            {error && (
              <div style={{ display:"flex", gap:8, padding:"10px 14px", borderRadius:8, background:"var(--red-2)", border:"1px solid #fca5a5", fontSize:12, color:"var(--red)", fontWeight:600, alignItems:"center", textAlign:"left" }}>
                ⚠️ {error}
              </div>
            )}
            <div style={{ display:"flex", gap:12 }}>
              <button className="kpi-btn-secondary" style={{ flex:1 }} disabled={saving} onClick={() => { setError(""); setStep("form"); }}>
                ← Back to Edit
              </button>
              <button className="kpi-btn-primary" style={{ flex:1, background:"var(--red)", borderColor:"var(--red)" }} disabled={saving} onClick={() => handleSave(true)}>
                {saving ? (
                  <span style={{ display:"flex", alignItems:"center", gap:6, justifyContent:"center" }}>
                    <span style={{ width:12, height:12, borderRadius:"50%", border:"2px solid rgba(255,255,255,0.4)", borderTopColor:"#fff", animation:"kpiSpin 0.7s linear infinite", display:"inline-block" }} />
                    Saving…
                  </span>
                ) : "Yes, Save Anyway"}
              </button>
            </div>
          </div>
        )}

        {/* ══ FORM ══ */}
        {step === "form" && (
          <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

            {/* Uploaded doc badge */}
            {hasUploadedDoc && (
              <div style={{ borderRadius:10, overflow:"hidden", border:"1px solid #bfdbfe" }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 14px", background:"#eff6ff" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ fontSize:16 }}>📎</span>
                    <div>
                      <div style={{ fontWeight:700, fontSize:12, color:"var(--ink)" }}>File uploaded — saved as proof document</div>
                      <div style={{ fontSize:11, color:"var(--ink-3)", marginTop:2 }}>
                        {parsedMeta.uploadedFileMetadata.fileMetadata?.fileName}
                        {" · "}
                        {Math.round(Number(parsedMeta.uploadedFileMetadata.fileMetadata?.fileSize || 0) / 1024)} KB
                      </div>
                    </div>
                  </div>
                  <button onClick={() => { setStep("choose"); setParsedMeta(null); setError(""); }} style={{ background:"none", border:"none", cursor:"pointer", fontSize:11, color:"var(--ink-4)", fontFamily:"var(--font-sans)", padding:"2px 6px", flexShrink:0 }}>
                    ← try another file
                  </button>
                </div>
                <div style={{ padding:"8px 14px", background:"var(--surface)", fontSize:11, color:"var(--ink-3)" }}>
                  ℹ️ Fill in the data fields below manually — this document will be attached as proof.
                </div>
              </div>
            )}

            {!hasParsed && (
              <button onClick={() => setStep("choose")} style={{ background:"none", border:"none", cursor:"pointer", fontSize:11, color:"var(--accent)", fontWeight:700, padding:0, fontFamily:"var(--font-sans)", alignSelf:"flex-start" }}>
                ← Upload file instead
              </button>
            )}

            {/* ── EMISSION CALCULATION METHOD ───────────────────────────────
                Shown only for EMISSION / EMISSION_ENERGY KPI types.
                Logic:
                  1. Fetch emissionKpiSettings for this category.
                  2. If settings has calculation_methods: ["X"] → show only X.
                  3. If settings has calculation_methods: ["X","Y"] → show both.
                  4. If settings not found / empty array → show ALL methods.
                  5. User must pick ONE method before saving.
            ── */}
            {isEmissionKpi && (
              <div style={{ padding:"14px 16px", background:"#f8fafc", border:"1.5px solid #e2e8f0", borderRadius:10 }}>
                {!emissionSettingsLoaded ? (
                  <div style={{ display:"flex", alignItems:"center", gap:8, fontSize:12, color:"var(--ink-4)" }}>
                    <span style={{ width:12, height:12, border:"2px solid #e2e8f0", borderTopColor:"#1abc9c", borderRadius:"50%", display:"inline-block", animation:"kpiSpin 0.7s linear infinite" }} />
                    Loading calculation methods…
                  </div>
                ) : (
                  <EmissionMethodPicker
                    availableMethods={availableMethodIds}
                    selected={selectedCalcMethod}
                    onChange={setSelectedCalcMethod}
                    categoryName={emissionCategoryName}
                    scopeName={emissionScopeName}
                  />
                )}
              </div>
            )}

            {/* ── LOCATION ── */}
            <div ref={locRef}>
              <FieldLabel required>Location</FieldLabel>
              <div className="kpi-input" style={{ cursor:"pointer", position:"relative", userSelect:"none" }} onClick={() => setOpenLocDD(p => !p)}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <span style={{ fontSize:13, color:form.location_ids.length?"var(--ink)":"var(--ink-4)" }}>{locLabel()}</span>
                  <span style={{ fontSize:10, color:"var(--ink-4)" }}>▾</span>
                </div>
                {openLocDD && (
                  <div style={{ position:"absolute", top:"110%", left:0, right:0, background:"var(--surface)", border:"1.5px solid var(--rule)", borderRadius:10, maxHeight:200, overflowY:"auto", zIndex:50, padding:8, boxShadow:"0 8px 24px rgba(0,0,0,0.12)" }} onClick={e => e.stopPropagation()}>
                    <label style={{ display:"flex", gap:8, padding:"6px 8px", fontWeight:700, fontSize:12, cursor:"pointer", borderRadius:6, alignItems:"center" }}>
                      <input type="checkbox" checked={form.location_ids.length === locations.length && locations.length > 0} onChange={toggleAllLocs} />
                      Select All
                    </label>
                    <div style={{ height:1, background:"var(--rule)", margin:"4px 0" }} />
                    {locations.map(l => (
                      <label key={l.id} style={{ display:"flex", gap:8, padding:"6px 8px", fontSize:12, cursor:"pointer", borderRadius:6, alignItems:"center" }}>
                        <input type="checkbox" checked={form.location_ids.includes(l.id)} onChange={() => toggleLoc(l.id)} />
                        <span style={{ flex:1 }}>{l.name}</span>
                        {l.location?.subLocation?.length > 0 && (
                          <span style={{ fontSize:9, fontWeight:700, padding:"1px 5px", borderRadius:4, background:"var(--accent-3)", color:"var(--accent)", fontFamily:"var(--font-mono)" }}>
                            {l.location.subLocation.length} sub
                          </span>
                        )}
                      </label>
                    ))}
                    {!locations.length && <div style={{ padding:"10px 8px", fontSize:12, color:"var(--ink-4)", textAlign:"center" }}>No locations available</div>}
                  </div>
                )}
              </div>
            </div>

            {/* ── SUB-LOCATION ── */}
            {hasSubLocs && (
              <div ref={subLocRef}>
                <FieldLabel>Sub-Location</FieldLabel>
                <div className="kpi-input" style={{ cursor:"pointer", position:"relative", userSelect:"none" }} onClick={() => setOpenSubLocDD(p => !p)}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <span style={{ fontSize:13, color:form.sub_location_ids.length?"var(--ink)":"var(--ink-4)" }}>{subLocLabel()}</span>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      {form.sub_location_ids.length > 0 && (
                        <span onClick={e => { e.stopPropagation(); setField("sub_location_ids", []); }} style={{ fontSize:12, color:"var(--ink-4)", cursor:"pointer", lineHeight:1 }} title="Clear">✕</span>
                      )}
                      <span style={{ fontSize:10, color:"var(--ink-4)" }}>▾</span>
                    </div>
                  </div>
                  {openSubLocDD && (
                    <div style={{ position:"absolute", top:"110%", left:0, right:0, background:"var(--surface)", border:"1.5px solid var(--rule)", borderRadius:10, maxHeight:200, overflowY:"auto", zIndex:50, padding:8, boxShadow:"0 8px 24px rgba(0,0,0,0.12)" }} onClick={e => e.stopPropagation()}>
                      <label style={{ display:"flex", gap:8, padding:"6px 8px", fontWeight:700, fontSize:12, cursor:"pointer", borderRadius:6, alignItems:"center" }}>
                        <input type="checkbox" checked={form.sub_location_ids.length === activeSubLocations.length && activeSubLocations.length > 0} onChange={toggleAllSubLocs} />
                        Select All Sub-Locations
                      </label>
                      <div style={{ height:1, background:"var(--rule)", margin:"4px 0" }} />
                      {activeSubLocGroups.map((group, gi) => (
                        <div key={group.locId}>
                          {activeSubLocGroups.length > 1 && (
                            <div style={{ padding:"4px 8px 2px", fontSize:9.5, fontWeight:700, color:"var(--ink-4)", textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:"var(--font-mono)", ...(gi > 0 ? { marginTop:4, borderTop:"1px solid var(--rule-2)", paddingTop:8 } : {}) }}>
                              {group.locName}
                            </div>
                          )}
                          {group.subs.map(sl => (
                            <label key={sl.id} style={{ display:"flex", gap:8, padding:"6px 8px", fontSize:12, cursor:"pointer", borderRadius:6, alignItems:"center", background:form.sub_location_ids.includes(sl.id)?"var(--accent-3)":"transparent" }}>
                              <input type="checkbox" checked={form.sub_location_ids.includes(sl.id)} onChange={() => toggleSubLoc(sl.id)} />
                              <span style={{ flex:1, fontWeight:form.sub_location_ids.includes(sl.id)?700:400, color:form.sub_location_ids.includes(sl.id)?"var(--accent)":"var(--ink)" }}>
                                {`${group.locName}-${sl.subLocation}`}
                              </span>
                              {form.sub_location_ids.includes(sl.id) && <span style={{ fontSize:10, color:"var(--accent)" }}>✓</span>}
                            </label>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ fontSize:10, color:"var(--ink-4)", marginTop:4 }}>Optional — leave blank to apply to all sub-locations</div>
              </div>
            )}

            {/* ── DATE RANGE ── */}
            <div>
              <FieldLabel required>Date Range</FieldLabel>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                <div>
                  <div style={{ fontSize:10, color:"var(--ink-4)", fontFamily:"var(--font-mono)", marginBottom:4 }}>FROM</div>
                  <input type="date" className="kpi-input" style={isWarn("from_date")?{borderColor:"var(--amber)",background:"var(--amber-2)"}:{}} value={form.from_date} onChange={e => setField("from_date", e.target.value)} />
                </div>
                <div>
                  <div style={{ fontSize:10, color:"var(--ink-4)", fontFamily:"var(--font-mono)", marginBottom:4 }}>TO</div>
                  <input type="date" className="kpi-input" style={isWarn("to_date")?{borderColor:"var(--amber)",background:"var(--amber-2)"}:{}} value={form.to_date} onChange={e => setField("to_date", e.target.value)} />
                </div>
              </div>
            </div>

            {/* ── VALUE + UNIT ── */}
            <div>
              <FieldLabel required>Value</FieldLabel>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 120px", gap:8 }}>
                <input type="number" className="kpi-input" style={isWarn("value")?{borderColor:"var(--amber)",background:"var(--amber-2)"}:{}} placeholder="Enter numeric value" value={form.value} onChange={e => setField("value", e.target.value)} />
                <select className="kpi-input" value={form.unit} onChange={e => setField("unit", e.target.value)}>
                  {resolvedUnits.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              {isWarn("value") && <div style={{ fontSize:11, color:"var(--amber)", marginTop:4, fontWeight:600 }}>Value not found in file — enter manually</div>}
            </div>

            {error && (
              <div style={{ display:"flex", gap:8, padding:"10px 14px", borderRadius:8, background:"var(--red-2)", border:"1px solid #fca5a5", fontSize:12, color:"var(--red)", fontWeight:600, alignItems:"center" }}>
                ⚠️ {error}
              </div>
            )}

            <div style={{ display:"flex", justifyContent:"flex-end", gap:8, paddingTop:4 }}>
              <button className="kpi-btn-secondary" onClick={onHide}>Cancel</button>
              <button className="kpi-btn-primary" onClick={() => handleSave(false)} disabled={saving || (isEmissionKpi && emissionSettingsLoaded && !selectedCalcMethod)} style={{ minWidth:90 }}>
                {saving ? (
                  <span style={{ display:"flex", alignItems:"center", gap:6 }}>
                    <span style={{ width:12, height:12, borderRadius:"50%", border:"2px solid rgba(255,255,255,0.4)", borderTopColor:"#fff", animation:"kpiSpin 0.7s linear infinite", display:"inline-block" }} />
                    Saving…
                  </span>
                ) : "Save"}
              </button>
            </div>
          </div>
        )}
      </div>
    </KpiModal>
  );
};

export default AddDataModal;